import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollReveal from '@/components/base/ScrollReveal';
import JsonLd from '@/components/base/JsonLd';

const siteUrl = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL) || 'https://5vix.com';

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '5vix',
  url: siteUrl,
  description: 'Integrated gym management system for membership, scheduling, billing, attendance, and analytics.',
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+201044553254',
    contactType: 'customer support',
    availableLanguage: ['English', 'Arabic'],
  },
  sameAs: [
    'https://www.instagram.com/5vixofficial/',
    'https://www.tiktok.com/@5vixofficial',
    'https://www.youtube.com/@5vix-c3p',
    'https://www.linkedin.com/in/5vix-software-12058b363/',
  ],
};

export default function TermsPage() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [isRTL, i18n.language]);

  const pageJsonLd = [
    organizationSchema,
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Terms & Conditions - 5vix',
      description: 'Terms and conditions for using 5vix gym management platform.',
    },
  ];

  return (
    <div className="bg-background-50 min-h-screen">
      <JsonLd data={pageJsonLd} />
      <Navbar />

      {/* Hero */}
      <section className="relative w-full min-h-[280px] md:min-h-[320px] flex items-center overflow-hidden bg-background-100">
        <div className="w-full px-4 md:px-6 py-14 md:py-16">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950 mb-4">
              {t('terms.title')}
            </h1>
            <p className="text-foreground-500 text-sm">
              {t('terms.lastUpdated')}
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <ScrollReveal>
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="space-y-12">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
              <div key={n}>
                <h2 className="text-xl font-heading font-bold text-foreground-950 mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center shrink-0">
                    <span className="text-primary-600 font-bold text-sm">{n}</span>
                  </span>
                  {t(`terms.section${n}.title`)}
                </h2>

                {n === 1 && (
                  <div className="pl-11 rtl:pl-0 rtl:pr-11 space-y-3">
                    <p className="text-foreground-700 text-sm leading-relaxed">
                      {t('terms.section1.content1')}
                    </p>
                    <p className="text-foreground-700 text-sm leading-relaxed">
                      {t('terms.section1.content2')}
                    </p>
                  </div>
                )}

                {n === 2 && (
                  <div className="pl-11 rtl:pl-0 rtl:pr-11 space-y-3">
                    <p className="text-foreground-700 text-sm leading-relaxed">
                      {t('terms.section2.content')}
                    </p>
                    <ul className="space-y-2">
                      {[1, 2, 3, 4].map((i) => (
                        <li key={i} className="flex items-start text-foreground-700 text-sm">
                          <span className="text-primary-500 mr-2 rtl:mr-0 rtl:ml-2 shrink-0">&bull;</span>
                          {t(`terms.section2.item${i}`)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {n === 3 && (
                  <div className="pl-11 rtl:pl-0 rtl:pr-11 space-y-3">
                    <p className="text-foreground-700 text-sm leading-relaxed">
                      {t('terms.section3.content')}
                    </p>
                    <ul className="space-y-2">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <li key={i} className="flex items-start text-foreground-700 text-sm">
                          <span className="text-primary-500 mr-2 rtl:mr-0 rtl:ml-2 shrink-0">&bull;</span>
                          {t(`terms.section3.item${i}`)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {n === 4 && (
                  <div className="pl-11 rtl:pl-0 rtl:pr-11 space-y-3">
                    <p className="text-foreground-700 text-sm leading-relaxed">
                      {t('terms.section4.content1')}
                    </p>
                    <p className="text-foreground-700 text-sm leading-relaxed">
                      {t('terms.section4.content2')}
                    </p>
                  </div>
                )}

                {n === 5 && (
                  <div className="pl-11 rtl:pl-0 rtl:pr-11 space-y-3">
                    <p className="text-foreground-700 text-sm leading-relaxed">
                      {t('terms.section5.content')}
                    </p>
                    <ul className="space-y-2">
                      {[1, 2, 3, 4].map((i) => (
                        <li key={i} className="flex items-start text-foreground-700 text-sm">
                          <span className="text-primary-500 mr-2 rtl:mr-0 rtl:ml-2 shrink-0">&bull;</span>
                          {t(`terms.section5.item${i}`)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {n >= 6 && n <= 7 && (
                  <div className="pl-11 rtl:pl-0 rtl:pr-11">
                    <p className="text-foreground-700 text-sm leading-relaxed">
                      {t(`terms.section${n}.content`)}
                    </p>
                  </div>
                )}

                {n === 8 && (
                  <div className="pl-11 rtl:pl-0 rtl:pr-11 space-y-3">
                    <p className="text-foreground-700 text-sm leading-relaxed">
                      {t('terms.section8.content')}
                    </p>
                    <div className="bg-background-100 rounded-xl p-5 border border-background-200/60 mt-4">
                      <p className="text-foreground-700 text-sm mb-2">
                        <strong>{t('terms.section8.whatsapp')}</strong>{' '}
                        <a href="https://wa.me/+201044553254" target="_blank" rel="nofollow noopener noreferrer" className="text-primary-600 hover:text-primary-700 transition-colors">+201044553254</a>
                      </p>
                      <p className="text-foreground-700 text-sm">
                        <strong>{t('terms.section8.phone')}</strong>{' '}
                        <a href="tel:201044553254" className="text-primary-600 hover:text-primary-700 transition-colors">01044553254</a>
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* CTA */}
      <ScrollReveal>
      <section className="py-14 md:py-20 bg-primary-500">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-2xl md:text-4xl font-heading font-bold text-background-50 mb-5">
            {t('terms.cta.title')}
          </h2>
          <p className="text-background-50/85 text-base md:text-lg mb-8 leading-relaxed">
            {t('terms.cta.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/#register"
              className="bg-background-50 text-primary-600 px-8 py-3.5 rounded-lg hover:bg-background-100 transition-all whitespace-nowrap cursor-pointer font-semibold text-sm"
            >
              {t('nav.tryFree')}
            </Link>
            <Link
              to="/help-center"
              className="bg-background-50/10 text-background-50 border-2 border-background-50/40 px-8 py-3.5 rounded-lg hover:bg-background-50/20 transition-all whitespace-nowrap cursor-pointer font-semibold text-sm"
            >
              {t('terms.cta.helpCenter')}
            </Link>
          </div>
        </div>
      </section>
      </ScrollReveal>

      <Footer />
    </div>
  );
}